import React from "react";
import { FormSectionElements, FormSections, ProgramContainer } from "src/entities/formTemplateDataType";
import FormValidationUtils from "../../utilities/FormValidationUtils";
import { replaceHoldersInRichText } from "../../utilities/HtmlUtils";
import { getFieldByName, getParentSection, getRootComponent } from '../../utilities/ElementUtils';
import TimeEstimateCalloutComponent from "../odsCalloutBox/TimeEstimateComponent";
import axios from "axios";
import { cleanup } from "../../security/PublicSecure";
import SectionUtils from "../../utilities/SectionUtils";
import DataUtils from "../../utilities/DataUtils";
import { se } from "date-fns/locale";
import { getEnvVar } from "../../config/RuntimeConfig";

interface Props {
  field: FormSectionElements;
  editable: boolean;
  title;
  labelControlArray;
  requiredField;
  callbackElement;
  isEng: boolean;
  value?: any;
  callbackStepIndex?: (index: number, currentIndex?: number) => void;
}

interface State {
  isVisible: boolean;

}
const goToStepClickHandlers = new WeakMap<Element, EventListener>();
const goToStepContextHandlers = new WeakMap<Element, EventListener>();
const persistedRichTextStyles = new Set<string>();

export class RichTextComponent extends React.Component<Props, State> {

  private richTextRef = React.createRef<HTMLDivElement>();
  private lastScriptValue?: string;

  constructor(props: Props) {
    super(props);
    let field = this.props.field;
    this.state = {
      isVisible: field.Visible
    };
    this.runFieldScript();
  }

  componentDidMount(): void {
    this.runFieldScript();
    this.addGoToStepAction();
    this.integrateCxpHandler();
    this.navigateToStepHandler();
    this.addScrollToSubsectionHandler();
    this.persistInlineStyles();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    if (prevProps.field.Script !== this.props.field.Script) {
      this.runFieldScript();
    }
    this.cleanupGotoStepListeners();
    this.addGoToStepAction();
    this.integrateCxpHandler();
    this.navigateToStepHandler();
    this.addScrollToSubsectionHandler();
    this.persistInlineStyles();
  }

  componentWillUnmount(): void {
    this.cleanupGotoStepListeners();
    const links = document.querySelectorAll('a.cxp-integration');
    if (links.length) {
      links.forEach((link) => {
        link.removeEventListener('click', this.handleCxpLinkClick);
      });
    }

      const nextStepLinks = document.querySelectorAll('a#navigate-to-this-step');
      if (nextStepLinks?.length) {
          nextStepLinks.forEach((link) => {
              link.removeEventListener('click', this.navigateToNextStepClick);
          });
      }

      // Remove event delegation handler for subsection scrolling
      const container = this.richTextRef.current;
      if (container && container.hasAttribute('data-subsection-scroll-handler-attached')) {
          container.removeAttribute('data-subsection-scroll-handler-attached');
      }
  }

  cleanupGotoStepListeners = () => {
    const container = this.richTextRef.current;
    if (!container) return;

    const buttons = container.getElementsByClassName('gotoStep');
    for (let i = 0; i < buttons.length; i++) {
      const button = buttons.item(i);
      if (!button) continue;

      const clickHandler = goToStepClickHandlers.get(button);
      if (clickHandler) {
        button.removeEventListener('click', clickHandler);
        goToStepClickHandlers.delete(button);
      }

      const contextHandler = goToStepContextHandlers.get(button);
      if (contextHandler) {
        button.removeEventListener('contextmenu', contextHandler);
        goToStepContextHandlers.delete(button);
      }
    }
  }

  private runFieldScript = () => {
    const script = this.props.field?.Script;
    if (typeof script !== 'string' || script.trim().length === 0) {
      return;
    }
    if (script === this.lastScriptValue) {
      return;
    }
    this.lastScriptValue = script;
    FormValidationUtils.runJavascript(this.props.field);
  }

  private persistInlineStyles = () => {
    const container = this.richTextRef.current;
    if (!container || typeof document === 'undefined') return;

    const styleNodes = container.querySelectorAll('style');
    styleNodes.forEach(node => {
      const content = node.textContent?.trim();
      if (!content) return;

      if (!persistedRichTextStyles.has(content)) {
        const styleEl = document.createElement('style');
        styleEl.setAttribute('data-richtext-style', 'true');
        styleEl.textContent = content;
        document.head.appendChild(styleEl);
        persistedRichTextStyles.add(content);
      }
      // Do not remove the original <style> node from the React-managed DOM tree
      // to avoid inconsistencies between React's virtual DOM and the real DOM.
    });
  }

  addGoToStepAction = () => {
    const container = this.richTextRef.current;
    if (!container) return;

    const buttons = container.getElementsByClassName('gotoStep');

    const section = getParentSection(this.props.field) as FormSections;
    const programContainer = getRootComponent(this.props.field) as ProgramContainer;
    const currentIndex = programContainer.ListOfFormSections.findIndex(p => p.Id == section.Id);

    for (let i = 0; i < buttons.length; i++) {
      const button = buttons.item(i);
      if (!button) continue;
      const indexAttr = button.getAttribute("data-index");
      if (indexAttr) {
        const nextIndex = Number.parseInt(indexAttr);       

        const resetSection =() => {
          const defaultData: any = {};
      		programContainer.Entity = {...defaultData};  // To force React to recognize the data reset.
          DataUtils.assignFormValue(programContainer);
          programContainer.ListOfFormSections[nextIndex].SectionReadonly = false;  // Allow to input data.
        }

        const clickHandler = (e: Event) => {
          e.preventDefault();
          const isFirstUse = button.getAttribute("data-state") === 'new';
          if (isFirstUse) { 
            section.ENUTitle=section.pageName; //for OER Self-Tracker issue
            //clean all of data records using submit button / back button back to OER Self-Tracker page
            DataUtils.clearFormValue(programContainer);
            resetSection();
            button.setAttribute("data-state", "processed"); // Prevent infinite callback loops.
          }

          const unlockSection = () => {
            resetSection();
            programContainer.ListOfFormSections[nextIndex].SectionReadonly = false;
          };

          const scrollTarget = this.richTextRef.current;
          const focusTarget: HTMLElement | null = scrollTarget?.querySelector('.hero') as HTMLElement;
          
          const navigate = () => {
            this.props.callbackStepIndex(nextIndex, currentIndex);
            unlockSection();
          };

          if (focusTarget && typeof focusTarget.scrollIntoView === 'function') {
            focusTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setTimeout(navigate, 0);
          } else if (scrollTarget?.scrollIntoView) {
            scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setTimeout(navigate, 0);
          } else {
            navigate();
          }
        };

        this.registerGotoStepHandler(button, clickHandler, goToStepClickHandlers);

        const contextHandler = (e: Event) => { e.preventDefault(); };
        this.registerGotoStepHandler(button, contextHandler, goToStepContextHandlers, 'contextmenu');
      }
    }
  }

  registerGotoStepHandler = (element: Element, handler: EventListener, map: WeakMap<Element, EventListener>, eventName: string = 'click') => {
    const existingHandler = map.get(element);
    if (existingHandler) {
      element.removeEventListener(eventName, existingHandler);
    }
    element.addEventListener(eventName, handler);
    map.set(element, handler);
  }

  handleProcess = async (fieldName: string) => {
    const root = getRootComponent(this.props.field);
    const profile = root?.Profile;
    const bindField = getFieldByName(fieldName, this.props.field);
    if (root.Profile?.email && bindField) {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${profile.token}`,
      }
      const value = bindField.Value;
      const response = await axios.post(`${getEnvVar('PUBLIC_OTRIS_API_BASE_PATH')}/transfer-log`, {
        payeeId: bindField.Value,
        email: profile.email,
      }, { headers });

      if (response.status == 200) {
        return response.data.webResponse?.statusCode === 0;
      } 
    }
    return false;
  };

  handleCxpLinkClick = async (e) => {
    e.preventDefault();
    const link = e.target;
    const path = link.dataset.path;
    if (path) {
      const fieldName = link.dataset.field;
      if (fieldName) {
        await this.handleProcess(fieldName);
      }
      const url = `${getEnvVar('PUBLIC_CXP_WEB_BASE_PATH')}/auth/signin?redirectUri=${this.props.isEng? '' : '/fr'}/${path}`;
      console.log("Redirecting to:", url);
      const root = getRootComponent(this.props.field);
      cleanup(root.CategoryId.toLowerCase(), root);
      window.location.replace(url);
    }
  }

  integrateCxpHandler = () => {
    const links = document.querySelectorAll('a.cxp-integration');
    if (links.length) {
      links.forEach((link) => {
        link.addEventListener('click', this.handleCxpLinkClick);
      });
    }
  }

    navigateToStepHandler = () => {
        const nextStepLinks = document.querySelectorAll('a#navigate-to-this-step');
        if (nextStepLinks?.length) {
            nextStepLinks.forEach((link) => {
                link.addEventListener('click', this.navigateToNextStepClick);
            });
        }
    }

    navigateToNextStepClick = (e: any) => {
        e.preventDefault();
        const link = e.target;
        const stepIndex = parseInt(link?.dataset?.stepIndex);

        if (!isNaN(stepIndex)) {
            if (this.props?.callbackStepIndex) {
                this.props.callbackStepIndex(stepIndex);
            } else {
                console.log('Unable to navigate to step, callbackStepIndex is not defined.');
            }
        } else {
            console.log('Unable to navigate to step, stepIndex is not defined.');
        }
    }

    scrollToSubsection = (e: any) => {
        e.preventDefault();
        const link = e.currentTarget || e.target;
        const subsectionTitle = link?.dataset?.subsectionTitle || link?.getAttribute('data-subsection-title');
        const subsectionId = link?.dataset?.subsectionId || link?.getAttribute('data-subsection-id');
        
        if (!subsectionTitle && !subsectionId) {
            return;
        }
        
        const section = this.props.field.parent.parent as FormSections;
        const root = getRootComponent(this.props.field);
        
        let subsection = null;
        if (subsectionId) {
            for (const subSec of section?.ListOfFormSubSections || []) {
              if (subSec.Visible !== false && (subSec.Id === subsectionId)) {
                subsection = subSec;
                break;
              }
            }
        } else if (subsectionTitle) {
            for (const subSec of section?.ListOfFormSubSections || []) {
              // For subsection title that is empty, match against its element titles.
              if (subSec.Visible !== false && ((subSec.ENUTitle === subsectionTitle || subSec.FRATitle === subsectionTitle || subSec.ListOfFormSectionElements?.some(ele => ele.ENUTitle === subsectionTitle || ele.FRATitle === subsectionTitle)))) {
                subsection = subSec;
                break;
              }
            }
        }
        
        if (!subsection) {
            return;
        }
        
        let targetElement: HTMLElement | null = null;
        const firstElement = subsection.ListOfFormSectionElements
            ?.filter(ele => ele.Visible !== false)
            .sort((a, b) => (a.Sequence || 0) - (b.Sequence || 0))[0];
        
        if (firstElement?.LoadBindName) {
            targetElement = document.querySelector(`input[name="${firstElement.LoadBindName}"]`) as HTMLInputElement;
            if (!targetElement) {
                targetElement = document.querySelector(`select[name="${firstElement.LoadBindName}"]`) as HTMLSelectElement;
            }
            if (!targetElement) {
                targetElement = document.querySelector(`textarea[name="${firstElement.LoadBindName}"]`) as HTMLTextAreaElement;
            }
            if (!targetElement) {
                targetElement = document.querySelector(`input[id="${firstElement.LoadBindName}"]`) as HTMLInputElement;
            }
            if (!targetElement) {
                targetElement = document.querySelector(`label[for="${firstElement.LoadBindName}"]`) as HTMLLabelElement;
            }
            if (!targetElement) {
                targetElement = document.querySelector(`label[id="label_${firstElement.LoadBindName}"]`) as HTMLLabelElement;
            }
            if (!targetElement) {
                targetElement = document.getElementById(firstElement.LoadBindName);
            }
        }
        
        if (!targetElement && subsectionTitle) {
            const headingsSelector = [
              'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'legend', '.ontario-label', '.ontario-fieldset__legend', 'label'
            ].join(',');
            const allHeadings = document.querySelectorAll(headingsSelector);
            for (const heading of Array.from(allHeadings)) {
                const headingText = heading.textContent?.trim();
                if (headingText && (
                    headingText === subsectionTitle || 
                    headingText.includes(subsectionTitle) || 
                    subsectionTitle.includes(headingText)
                )) {
                    targetElement = heading as HTMLElement;
                    break;
                }
            }
        }
        
        if (!targetElement && firstElement) {
            const sectionId = section?.Id;
            const subsectionSequence = subsection.Sequence;
            if (sectionId && subsectionSequence && firstElement.Id) {
                const labelId = `Label-${sectionId}-${subsectionSequence}-${firstElement.Id}`;
                targetElement = document.getElementById(labelId);
            }
        }
        
        if (!targetElement && subsectionTitle) {
            const allElements = document.querySelectorAll('*');
            for (const el of Array.from(allElements)) {
                const htmlEl = el as HTMLElement;
                const text = htmlEl.textContent?.trim();
                if (text && text.includes(subsectionTitle) && htmlEl.offsetHeight > 0) {
                    targetElement = htmlEl;
                    break;
                }
            }
        }
        
        if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            if (targetElement instanceof HTMLInputElement || 
                targetElement instanceof HTMLSelectElement || 
                targetElement instanceof HTMLTextAreaElement) {
                setTimeout(() => targetElement.focus(), 100);
            }
        }
    }

    addScrollToSubsectionHandler = () => {
        const container = this.richTextRef.current;
        if (!container) {
            return;
        }
        
        if (!container.hasAttribute('data-subsection-scroll-handler-attached')) {
            container.addEventListener('click', (e: any) => {
                const target = e.target;
                let link = target;
                while (link && link !== container) {
                    if (link.classList && link.classList.contains('scroll-to-subsection')) {
                        const linkEvent = {
                            ...e,
                            currentTarget: link,
                            target: target,
                            preventDefault: () => e.preventDefault()
                        };
                        this.scrollToSubsection(linkEvent);
                        break;
                    }
                    link = link.parentElement;
                }
            });
            container.setAttribute('data-subsection-scroll-handler-attached', 'true');
        }
    }

  render() {
    const HtmlToReactParser = require('html-to-react').Parser;
    const htmlToReactParser = new HtmlToReactParser();

    const field = this.props.field;
    if (!field.Visible) return null;

    let controlArray = [];
    let displayfield = true;

    if (field.FilterField != null && (Array.isArray(field.FilterField) ? field.FilterField.length > 0 : String(field.FilterField).length > 0)) {
      const filterFields: string[] = Array.isArray(field.FilterField) ? field.FilterField.filter(Boolean) : [String(field.FilterField)];

      const results: boolean[] = [];

      filterFields.forEach((filterField) => {
        const dependent = getFieldByName(filterField, field);

        const configured = field.FilterValue?.[filterField];  // Should only compare with the english value.
        if (configured == null || (Array.isArray(configured) && configured.length === 0)) {
          return;
        }

        if (!dependent) {
          results.push(false);
          return;
        }

        const current = dependent.Value;
        const curArr = current == null ? [] : (Array.isArray(current) ? current.map(String) : [String(current)]);
        const cfgArr = Array.isArray(configured) ? configured.map(String) : [String(configured)];

        results.push(curArr.some(v => cfgArr.includes(v)));
      });

      displayfield = results.length > 0 ? results.every(Boolean) : true;
    }

    if (displayfield) {
      if (!this.state.isVisible) this.setState({ isVisible: true });
      const txt = (this.props.isEng ? field.Value : field.ValueFRA) ?? (this.props.value || "");
      const root = getRootComponent(field);
      const replacement = replaceHoldersInRichText(root?.CategoryId, txt, field, this.props.isEng ? 'en' : 'fr');
      const reactElement = htmlToReactParser.parse(replacement);
      let labelContent = [];
      
      if (field.wrapCalloutbox) {
        labelContent.push(
          <div id={`Label-${field.parent.parent.Id}-${field.parent.Sequence}-${field.Id}`} key={`key-${field.parent.parent.Id}-${field.parent.Sequence}-${field.Id}`}
            className="callout-component"
          >
            {reactElement}
          </div>
        );
      } else {
        labelContent.push(
          <div id={`Label-${this.props.field.parent.parent.Id}-${this.props.field.parent.Sequence}-${this.props.field.Id}` } key={`key-${this.props.field.parent.parent.Id}-${this.props.field.parent.Sequence}-${this.props.field.Id}`}
            style={{ marginTop: '2rem', alignContent: 'center', alignItems: 'center', width: '100%' }}
          >
            {reactElement}
          </div>
        );
      }
      let wrapBoxType; // = isFirstElement(field) ? 'ontario-margin-top-24-!' : isLastElement(field) ? 'ontario-margin-bottom-24-!' : '';

      switch (field.wrapCalloutbox) {
        case 'general':
          wrapBoxType = "ontario-callout callout-max-width";
          break;
        case 'timeEstimate':
          labelContent = [];
          labelContent.push(
            <TimeEstimateCalloutComponent field={field}>{reactElement}</TimeEstimateCalloutComponent>
          );
          break;
        case 'informational':
          wrapBoxType = "ontario-alert ontario-alert--informational callout-max-width";
          break;
        case 'warning':
          wrapBoxType = "ontario-alert ontario-alert--warning callout-max-width";
          break;
        case 'success':
          wrapBoxType = "ontario-alert ontario-alert--success callout-max-width";
          break;
        case 'error':
          wrapBoxType = "ontario-alert ontario-alert--error callout-max-width";
          break;
        default:
          wrapBoxType = "";
      }

      controlArray.push(
        <div className={`${wrapBoxType ?? ''}`} key={`div-${field.parent.parent.Id}-${field.parent.Sequence}-${field.Id}`}>
          {labelContent}
        </div>
      );
    } else if (this.state.isVisible) {
      this.setState({ isVisible: false });
    }

    return (
      <div ref={this.richTextRef}>
        {controlArray}
      </div>
    );
  }
}
